import { clamp } from "@/utils/math"
import {
	defaultMaskAdjustments,
	getLocalAdjustmentRange,
	getDefaultAdjustments,
	getAdjustmentRange,
} from "@/core/adjustments"
import {
	AdjustmentRange,
	Mask,
	MaskGroup,
	MaskGroupAdjustments,
	HealingItem,
	Adjustments,
	ColorProfileName,
} from "@/core/types"

/**
 * Check if a value is a valid finite number (not NaN, not Infinity).
 */
function isValidFiniteNumber(value: unknown): value is number {
	return typeof value === "number" && Number.isFinite(value)
}

/**
 * Keys that accept `boolean | null` type (default is null but boolean is valid).
 */
const nullableBooleanKeys: Set<string> = new Set(["enableLensCorrections"])

/**
 * Keys that accept `string | null` type (default is null but string is valid).
 */
const nullableStringKeys: Set<string> = new Set(["colorMatchImageId"])

const colorProfileValues = new Set(
	Object.values(ColorProfileName).filter((value) => typeof value === "number") as number[],
)

function isValidColorProfile(value: unknown): boolean {
	return value === null || (typeof value === "number" && colorProfileValues.has(value))
}

/**
 * Keys that are numeric arrays (curves, crop, ranges).
 */
const numericArrayKeys: Set<string> = new Set([
	"curvesAll",
	"curvesRed",
	"curvesGreen",
	"curvesBlue",
	"crop",
	"cropRatio",
	"defringePurpleHueRange",
	"defringeGreenHueRange",
])

/**
 * Known ranges for numeric array values.
 */
const numericArrayRanges: Record<string, AdjustmentRange> = {
	curvesAll: [0, 255],
	curvesRed: [0, 255],
	curvesGreen: [0, 255],
	curvesBlue: [0, 255],
	crop: [0, 1],
	defringePurpleHueRange: [194 / 360, 405 / 360],
	defringeGreenHueRange: [20 / 360, 220 / 360],
}

/**
 * Get the expected numeric range for a numeric array key.
 */
function getNumericArrayRange(key: string): AdjustmentRange | undefined {
	return numericArrayRanges[key]
}

/**
 * Validate that all numbers in an array (or nested array) are finite.
 */
function validateNumericArray(arr: unknown[], range?: AdjustmentRange): boolean {
	for (const item of arr) {
		if (Array.isArray(item)) {
			if (!validateNumericArray(item, range)) return false
		} else if (typeof item === "number") {
			if (!isValidFiniteNumber(item)) return false
			if (range && (item < range[0] || item > range[1])) return false
		} else {
			return false
		}
	}
	return true
}

/**
 * Sanitize numbers in an array by replacing NaN/Infinity with 0.
 */
function sanitizeNumericArray(
	arr: unknown[],
	defaultArr: unknown[],
	range?: AdjustmentRange,
): unknown[] {
	return arr.map((item, index) => {
		const defaultItem = defaultArr[index]
		if (Array.isArray(item) && Array.isArray(defaultItem)) {
			return sanitizeNumericArray(item, defaultItem, range)
		} else if (typeof item === "number") {
			if (!isValidFiniteNumber(item)) {
				return typeof defaultItem === "number" ? defaultItem : 0
			}
			if (range && (item < range[0] || item > range[1])) {
				return clamp(item, range[0], range[1])
			}
			return item
		}
		if (typeof defaultItem === "number") {
			return defaultItem
		}
		return item
	})
}

/**
 * Validate a single PointColorHSL object.
 * PointColorHSL: { id: string, color: [n,n,n], hueShift: n, satShift: n, lumShift: n, range: n,
 *                  hueRange: [n,n,n,n], satRange: [n,n,n,n], lumRange: [n,n,n,n] }
 */
function isValidPointColorHSL(item: unknown): boolean {
	if (typeof item !== "object" || item === null) return false

	const pc = item as Record<string, unknown>

	// Check string field
	if (typeof pc.id !== "string") return false

	// Check numeric fields
	const numericFields = ["hueShift", "satShift", "lumShift", "range"]
	for (const field of numericFields) {
		if (!isValidFiniteNumber(pc[field])) return false
	}

	// Check numeric array fields
	const arrayFields = ["color", "hueRange", "satRange", "lumRange"]
	for (const field of arrayFields) {
		if (!Array.isArray(pc[field])) return false
		if (!validateNumericArray(pc[field] as unknown[])) return false
	}

	return true
}

/**
 * Validate pointColors array (PointColorHSL[]).
 */
function validatePointColors(arr: unknown[]): boolean {
	for (const item of arr) {
		if (!isValidPointColorHSL(item)) return false
	}
	return true
}

/**
 * Validate vec2 (number[2]).
 */
function isValidVec2(value: unknown): boolean {
	return (
		Array.isArray(value) &&
		value.length === 2 &&
		isValidFiniteNumber(value[0]) &&
		isValidFiniteNumber(value[1])
	)
}

/**
 * Validate Rectangle object.
 */
function isValidRectangle(value: unknown): boolean {
	if (typeof value !== "object" || value === null) return false
	const rect = value as Record<string, unknown>
	return (
		isValidFiniteNumber(rect.x) &&
		isValidFiniteNumber(rect.y) &&
		isValidFiniteNumber(rect.width) &&
		isValidFiniteNumber(rect.height)
	)
}

/**
 * Validate BrushStroke object.
 */
function isValidBrushStroke(value: unknown): boolean {
	if (typeof value !== "object" || value === null) return false
	const stroke = value as Record<string, unknown>

	if (!Array.isArray(stroke.points)) return false
	for (const point of stroke.points) {
		if (!isValidFiniteNumber(point)) return false
	}

	const numericFields = ["size", "spacing", "flow", "feather", "density"]
	for (const field of numericFields) {
		if (!isValidFiniteNumber(stroke[field])) return false
	}

	const booleanFields = ["isErasing", "isColorAware"]
	for (const field of booleanFields) {
		if (typeof stroke[field] !== "boolean") return false
	}

	return true
}

/**
 * Validate Mask object.
 */
function isValidMask(value: unknown): value is Mask {
	if (typeof value !== "object" || value === null) return false
	const mask = value as Record<string, unknown>

	if (typeof mask.type !== "string") return false
	if (typeof mask.id !== "string") return false
	if (typeof mask.name !== "string") return false
	if (typeof mask.active !== "boolean") return false
	if (typeof mask.inverted !== "boolean") return false
	if (mask.blendMode !== "add" && mask.blendMode !== "subtract") return false

	switch (mask.type) {
		case "radial": {
			const numericFields = ["centerX", "centerY", "radiusX", "radiusY", "angle", "feather"]
			for (const field of numericFields) {
				if (!isValidFiniteNumber(mask[field])) return false
			}
			return true
		}
		case "gradient": {
			const numericFields = ["startX", "startY", "endX", "endY"]
			for (const field of numericFields) {
				if (!isValidFiniteNumber(mask[field])) return false
			}
			return true
		}
		case "brush": {
			if (!Array.isArray(mask.strokes)) return false
			for (const stroke of mask.strokes) {
				if (!isValidBrushStroke(stroke)) return false
			}
			return true
		}
		case "subject":
		case "people":
		case "sky":
		case "background":
			return true
		default:
			return false
	}
}

/**
 * Validate MaskGroup object.
 */
function isValidMaskGroup(value: unknown): value is MaskGroup {
	if (typeof value !== "object" || value === null) return false
	const group = value as Record<string, unknown>

	if (typeof group.id !== "string") return false
	if (typeof group.name !== "string") return false
	if (typeof group.active !== "boolean") return false
	if (!Array.isArray(group.masks)) return false
	for (const mask of group.masks) {
		if (!isValidMask(mask)) return false
	}

	for (const key of Object.keys(defaultMaskAdjustments) as (keyof MaskGroupAdjustments)[]) {
		const val = group[key]
		if (key === "pointColors") {
			if (!Array.isArray(val)) return false
			if (!validatePointColors(val)) return false
			continue
		}
		if (!isValidFiniteNumber(val)) return false
		const range = getLocalAdjustmentRange(key)
		if (val < range[0] || val > range[1]) return false
	}

	return true
}

/**
 * Validate MaskGroup array (MaskGroup[]).
 */
function validateMaskGroups(arr: unknown[]): boolean {
	for (const item of arr) {
		if (!isValidMaskGroup(item)) return false
	}
	return true
}

/**
 * Validate HealingItem object.
 */
function isValidHealingItem(value: unknown): value is HealingItem {
	if (typeof value !== "object" || value === null) return false
	const item = value as Record<string, unknown>

	if (typeof item.id !== "string") return false
	if (!Array.isArray(item.strokes)) return false
	for (const stroke of item.strokes) {
		if (!isValidBrushStroke(stroke)) return false
	}

	if (!isValidRectangle(item.strokesRect)) return false
	if (!isValidRectangle(item.sourceRect)) return false
	if (!isValidRectangle(item.destinationRect)) return false
	if (!isValidVec2(item.sourceCenter)) return false
	if (!isValidVec2(item.destinationCenter)) return false

	return true
}

/**
 * Validate HealingItem array (HealingItem[]).
 */
function validateHealingItems(arr: unknown[]): boolean {
	for (const item of arr) {
		if (!isValidHealingItem(item)) return false
	}
	return true
}

/**
 * Check if a value is valid for the given key, considering special nullable types.
 */
function isValidValueForKey(key: string, value: unknown, defaultValue: unknown): boolean {
	// Handle nullable boolean keys (boolean | null)
	if (nullableBooleanKeys.has(key)) {
		return value === null || typeof value === "boolean"
	}

	// Handle colorProfile enum or null
	if (key === "colorProfile") {
		return isValidColorProfile(value)
	}

	// Handle nullable string keys (string | null)
	if (nullableStringKeys.has(key)) {
		return value === null || typeof value === "string"
	}

	// Handle numeric arrays - type check only (values validated separately)
	if (numericArrayKeys.has(key)) {
		return Array.isArray(value)
	}

	// Handle pointColors array - type check only (values validated separately)
	if (key === "pointColors") {
		return Array.isArray(value)
	}

	// Handle localAdjustments array - type check only (values validated separately)
	if (key === "localAdjustments") {
		return Array.isArray(value)
	}

	// Handle healingMasks array - type check only (values validated separately)
	if (key === "healingMasks") {
		return Array.isArray(value)
	}

	// For numbers, must be finite (not NaN, not Infinity)
	if (typeof defaultValue === "number") {
		return isValidFiniteNumber(value)
	}

	// For booleans
	if (typeof defaultValue === "boolean") {
		return typeof value === "boolean"
	}

	// For strings
	if (typeof defaultValue === "string") {
		return typeof value === "string"
	}

	// For arrays (non-numeric, like localAdjustments, healingMasks)
	if (Array.isArray(defaultValue)) {
		return Array.isArray(value)
	}

	// For objects (non-array)
	if (typeof defaultValue === "object" && defaultValue !== null) {
		return typeof value === "object" && value !== null && !Array.isArray(value)
	}

	return true
}

/**
 * Sanitize a partial adjustments object by:
 * 1. Checking type correctness against default values
 * 2. Clamping out-of-range numeric values to their valid range
 * 3. Replacing invalid values (NaN, Infinity, wrong types) with defaults
 * 4. Sanitizing numeric arrays (curves, crop, etc.)
 *
 * @param adjustments - The partial adjustments to sanitize
 * @param defaults - Default adjustments to use for invalid values (optional, uses getDefaultAdjustments() if not provided)
 * @returns A new sanitized partial adjustments object
 */
export function sanitizeAdjustments(
	adjustments: Partial<Adjustments>,
	defaults?: Adjustments,
): Partial<Adjustments> & {
	sanitizedAdjustments: Partial<Adjustments>
	invalidAdjustments: Partial<Record<keyof Adjustments, unknown>>
} {
	const defaultAdjustments = defaults ?? getDefaultAdjustments()
	const sanitized: Partial<Adjustments> = { ...adjustments }
	const invalidAdjustments: Partial<Record<keyof Adjustments, unknown>> = {}

	for (const key of Object.keys(adjustments) as (keyof Adjustments)[]) {
		const value = adjustments[key]
		const defaultValue = defaultAdjustments[key]

		// Skip undefined values (not set in partial)
		if (value === undefined) {
			continue
		}

		// Check type validity
		if (!isValidValueForKey(key, value, defaultValue)) {
			// Invalid type or value - use default
			;(sanitized as Record<string, unknown>)[key] = defaultValue
			invalidAdjustments[key] = value
			console.warn(
				`[sanitizeAdjustments] Invalid type/value for "${key}": ${JSON.stringify(value)}, using default: ${JSON.stringify(defaultValue)}`,
			)
			continue
		}

		// For numeric arrays, sanitize any NaN/Infinity/out-of-range values
		if (numericArrayKeys.has(key) && Array.isArray(value) && Array.isArray(defaultValue)) {
			const range = getNumericArrayRange(key)
			if (!validateNumericArray(value, range)) {
				const sanitizedArray = sanitizeNumericArray(value, defaultValue, range)
				;(sanitized as Record<string, unknown>)[key] = sanitizedArray
				invalidAdjustments[key] = value
				console.warn(
					`[sanitizeAdjustments] Sanitized numeric array "${key}" with invalid numbers`,
				)
			}
		}

		// For pointColors array, reset to default if any invalid PointColorHSL items
		if (key === "pointColors") {
			if (!Array.isArray(value) || !validatePointColors(value)) {
				;(sanitized as Record<string, unknown>)[key] = defaultValue ?? []
				invalidAdjustments[key] = value
				console.warn(`[sanitizeAdjustments] Reset pointColors due to invalid items`)
			}
		}

		// For localAdjustments array, reset to default if any invalid MaskGroup items
		if (key === "localAdjustments") {
			if (!Array.isArray(value) || !validateMaskGroups(value)) {
				;(sanitized as Record<string, unknown>)[key] = defaultValue ?? []
				invalidAdjustments[key] = value
				console.warn(`[sanitizeAdjustments] Reset localAdjustments due to invalid items`)
			}
		}

		// For healingMasks array, reset to default if any invalid HealingItem items
		if (key === "healingMasks") {
			if (!Array.isArray(value) || !validateHealingItems(value)) {
				;(sanitized as Record<string, unknown>)[key] = defaultValue ?? []
				invalidAdjustments[key] = value
				console.warn(`[sanitizeAdjustments] Reset healingMasks due to invalid items`)
			}
		}

		// For numbers, also clamp to valid range
		if (typeof value === "number" && typeof defaultValue === "number") {
			const range = getAdjustmentRange(key)
			if (value < range[0] || value > range[1]) {
				const clampedValue = clamp(value, range[0], range[1])
				;(sanitized as Record<string, unknown>)[key] = clampedValue
				invalidAdjustments[key] = value
				console.warn(
					`[sanitizeAdjustments] Out of range value for "${key}": ${value}, clamped to: ${clampedValue} (range: [${range[0]}, ${range[1]}])`,
				)
			}
		}
	}

	return {
		...sanitized,
		sanitizedAdjustments: sanitized,
		invalidAdjustments,
	}
}

/**
 * Check if a partial adjustments object contains any invalid values.
 * Checks both type correctness and range validity for numbers.
 */
export function hasInvalidAdjustments(
	adjustments: Partial<Adjustments>,
	defaults?: Adjustments,
): boolean {
	const defaultAdjustments = defaults ?? getDefaultAdjustments()

	for (const key of Object.keys(adjustments) as (keyof Adjustments)[]) {
		const value = adjustments[key]
		const defaultValue = defaultAdjustments[key]

		if (value === undefined) {
			continue
		}

		// Check type validity
		if (!isValidValueForKey(key, value, defaultValue)) {
			return true
		}

		// For numeric arrays, check all numbers are valid and in range
		if (numericArrayKeys.has(key) && Array.isArray(value)) {
			const range = getNumericArrayRange(key)
			if (!validateNumericArray(value, range)) {
				return true
			}
		}

		// For pointColors arrays, validate each item
		if (key === "pointColors" && Array.isArray(value)) {
			if (!validatePointColors(value)) {
				return true
			}
		}

		// For localAdjustments arrays, validate each MaskGroup
		if (key === "localAdjustments" && Array.isArray(value)) {
			if (!validateMaskGroups(value)) {
				return true
			}
		}

		// For healingMasks arrays, validate each HealingItem
		if (key === "healingMasks" && Array.isArray(value)) {
			if (!validateHealingItems(value)) {
				return true
			}
		}

		// For numbers, also check range
		if (typeof value === "number" && typeof defaultValue === "number") {
			const range = getAdjustmentRange(key)
			if (value < range[0] || value > range[1]) {
				return true
			}
		}
	}

	return false
}
